import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  GlobeAltIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  direction: 'ltr' | 'rtl';
}

const languages: Language[] = [
  { 
    code: 'en', 
    name: 'English', 
    nativeName: 'English',
    flag: '🇬🇧',
    direction: 'ltr'
  },
  { 
    code: 'fr', 
    name: 'French', 
    nativeName: 'Français',
    flag: '🇫🇷',
    direction: 'ltr'
  },
  { 
    code: 'ar', 
    name: 'Arabic', 
    nativeName: 'العربية',
    flag: '🇸🇦',
    direction: 'rtl'
  }
];

interface LanguageSwitcherProps {
  compact?: boolean;
}

export default function LanguageSwitcher({ compact = false }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation(['common']);
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    const selectedLang = languages.find(lang => lang.code === languageCode);
    
    // Set document direction based on language
    if (selectedLang) {
      document.documentElement.dir = selectedLang.direction;
      document.documentElement.lang = languageCode;
    }
    
    // Save preference to localStorage
    localStorage.setItem('doctif_language', languageCode);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Set initial direction on mount
  React.useEffect(() => {
    const currentLang = languages.find(lang => lang.code === i18n.language);
    if (currentLang) {
      document.documentElement.dir = currentLang.direction;
      document.documentElement.lang = i18n.language;
    }
  }, [i18n.language]);

  if (compact) {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-sidebar-text hover:text-sidebar-text-active hover:bg-sidebar-item-hover transition-all duration-200"
          title={t('accessibility.changeLanguage')}
        >
          <GlobeAltIcon className="h-4 w-4" />
        </button>

        {isOpen && (
          <div className="absolute bottom-full left-0 mb-2 w-48 rounded-xl bg-card border border-border shadow-soft-xl z-50 overflow-hidden animate-slide-in">
            <div className="py-1">
              {languages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => changeLanguage(language.code)}
                  className={`
                    w-full flex items-center px-3 py-2 text-sm transition-colors duration-200
                    ${currentLanguage.code === language.code
                      ? 'bg-primary/10 text-primary'
                      : 'text-card-foreground hover:bg-muted'
                    }
                  `}
                >
                  <span className="text-lg mr-2">{language.flag}</span>
                  <span className="flex-1 text-left">{language.nativeName}</span>
                  {currentLanguage.code === language.code && (
                    <CheckIcon className="h-4 w-4" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-xl text-sidebar-text hover:text-sidebar-text-active hover:bg-sidebar-item-hover transition-all duration-200"
      >
        <GlobeAltIcon className="h-5 w-5" />
        <span className="text-sm font-medium">{currentLanguage.flag} {currentLanguage.nativeName}</span>
      </button>

      {isOpen && (
        <div className="absolute bottom-full left-0 mb-2 w-56 rounded-xl bg-card border border-border shadow-soft-xl z-50 overflow-hidden animate-slide-in">
          <div className="p-2 border-b border-border bg-muted/30">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-2 py-1">
              {t('accessibility.selectLanguage', { defaultValue: 'Select Language' })}
            </p>
          </div>
          <div className="py-1">
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => changeLanguage(language.code)}
                className={`
                  w-full flex items-center px-3 py-2.5 text-sm transition-colors duration-200
                  ${currentLanguage.code === language.code
                    ? 'bg-primary/10 text-primary'
                    : 'text-card-foreground hover:bg-muted'
                  }
                `}
              >
                <span className="text-xl mr-3">{language.flag}</span>
                <div className="flex-1 text-left">
                  <p className="font-medium">{language.nativeName}</p>
                  <p className="text-xs text-muted-foreground">{language.name}</p>
                </div>
                {currentLanguage.code === language.code && (
                  <CheckIcon className="h-4 w-4 ml-2" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
